/**
 * @file: This file holds the steps a Compose session can go back over.
 *
 * Nothing reaches the server before Save, so a step back writes nothing and
 * costs only memory. Every command that changes the rows, the pins or the seed
 * leaves a step behind, and Undo takes the last one off again.
 *
 * A step holds only the data the commands own. The name, the kind and the
 * blocker of a media come from the catalogue, so a step back reads the
 * catalogue of that moment and never an old copy of it.
 *
 * The reason to change this file is a change in how far back the session goes.
 * What a step holds is decided by `compose-state.js`, which puts it in.
 *
 * The class has no Angular dependency.
 */

/**
 * How many steps back the session holds. Nothing reaches the server before
 * Save, so a step back writes nothing. A deep stack uses only memory.
 */
const HISTORY_DEPTH = 30

class History {
  constructor (depth = HISTORY_DEPTH) {
    this._depth = depth
    this._steps = []
  }

  /**
   * Keep one step. The oldest step goes when the stack is full: an operator who
   * needs more than thirty steps back needs the stored playlist, not Undo.
   *
   * @param {object} step - What the caller needs to put the state back
   */
  remember (step) {
    this._steps.push(step)

    if (this._steps.length > this._depth) {
      this._steps.shift()
    }
  }

  /** The last step, or undefined when there is none. The stack is not changed. */
  last () {
    return this._steps[this._steps.length - 1]
  }

  /** Take the last step off the stack. */
  drop () {
    this._steps.pop()
  }

  canUndo () {
    return this._steps.length > 0
  }

  /** Forget every step. A step back into another playlist makes no sense. */
  clear () {
    this._steps = []
  }
}

export { History, HISTORY_DEPTH }
